import React, { useState } from "react";
import PropTypes from "prop-types";
import ButtonBase from "@material-ui/core/ButtonBase";
import Checkbox from "@material-ui/core/Checkbox";
import Grid from "@material-ui/core/Grid";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import MembershipActionFooter from "./MembershipActionFooter";
import Alert from "@material-ui/lab/Alert";
import PasswordComponent from "./MembershipPasswordComponent";
import { User } from "lucide-react";
import { InputAdornment } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
  checkbox: {
    paddingBottom: 0,
    paddingTop: 0,
  },
  marginTop: {
    marginTop: theme.spacing(2),
  },
  forgottenPasswordLabel: {
    marginRight: theme.spacing(4),
    color: "var(--gc-action-strong)",
    textDecoration: "none",
    fontWeight: 500,
    "&:hover": {
      textDecoration: "underline",
    },
  },
  gridItemFlex: {
    display: "flex",
  },
  gridItemFlexSpaceBetween: {
    justifyContent: "space-between",
  },
  inputWrapper: {
    flex: 1,
    marginRight: theme.spacing(2),
  },
  alert: {
    marginRight: theme.spacing(2),
  },
  textBold: {
    fontWeight: theme.typography.fontWeightBold,
  },
  textAction: {
    color: "var(--gc-action-strong)",
  },
  text: {
    color: "oklch(55.4% .046 257.417)",
  },
  inputLabel: {
    color: "#0f1729 !important",
    paddingLeft: "0 !important",
    display: "block",
    fontSize: "14px",
    fontWeight: "500",
    marginBottom: "4px",
  },
  input: {
    boxShadow:
      "0 0 #0000, 0 0 #0000, 0 0 #0000, 0 0 #0000, 0 1px 3px 0 #0000001a,0 1px 2px -1px #0000001a",
    background: "oklch(98.4% .003 247.858)",
    border: "1px solid oklch(92.9% .013 255.508)",
    "&.Mui-focused": {
      background: "#fff",
      outline: "hsl(45 93% 47%) solid 1px",
      "& input": {
        background: "#fff !important",
      },
    },
    "& input": {
      padding: "0px 12px 0px 0px",
      height: "36px",
      background: "oklch(98.4% .003 247.858) !important",
      border: "none !important",
    },
  },
  icon: {
    width: "16px",
    height: "16px",
    color: "oklch(70.4% .04 256.788)",
  },
}));

export default function MembershipLogin(props) {
  const {
    onLogin,
    onSignup,
    isSigningIn = false,
    hideForgottenPassword = false,
    error,
    source,
  } = props;
  // const errors = props.errors || {}
  const classes = useStyles();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [shouldSaveSession, setShouldSaveSession] = useState(false);

  let errorMessage;
  if (error) {
    const { data = {} } = error.response || {};
    if (data.code === 401) {
      errorMessage = data.message || "";
    } else {
      errorMessage = "Errore generico durante il tentativo di autenticazione.";
    }
  }

  function isLoginDisabled() {
    return (
      isSigningIn || email.trim().length === 0 || password.trim().length === 0
    );
  }

  function handleLoginClick() {
    onLogin(email, password, shouldSaveSession);
  }

  let title = "EFFETTUA IL LOGIN";
  let captionText = "Non hai ancora un account?";
  let onSecondaryAction = onSignup;
  let secondaryActionText = "Registrati ora";
  let subtitle;
  if (source === "main") {
    title = "SE SEI GIÀ REGISTRATO EFFETTUA L'ACCESSO";
    captionText = onSecondaryAction = secondaryActionText = null;
    subtitle =
      "Accedi in 2 click e la maggior parte dei campi del primo step saranno automaticamente completati.";
  }

  return (
    <div className="member-login">
      <Grid container direction="column" spacing={1}>
        <Grid item xs={12}>
          <Typography className={classes.textBold}>{title}</Typography>
          {subtitle && (
            <Typography
              variant="body2"
              className={`${classes.text} ${classes.marginTop}`}
            >
              {subtitle}
            </Typography>
          )}
        </Grid>
        <Grid
          container
          className={`${classes.gridItemFlex} ${classes.marginTop}`}
          item
          xs={12}
          spacing={2}
        >
          <Grid item xs={12} sm={6}>
            <label className={classes.inputLabel} htmlFor="member-email">
              Email *
            </label>
            <TextField
              className={classes.inputWrapper}
              InputProps={{
                disableUnderline: true,
                className: classes.input,
                startAdornment: (
                  <InputAdornment position="start">
                    <User className={classes.icon} />
                  </InputAdornment>
                ),
              }}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              value={email}
              placeholder="Email"
              type="email"
              variant="outlined"
              id="member-email"
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <PasswordComponent
              componentStyle={classes.inputWrapper}
              onChange={setPassword}
              value={password}
              placeholder="Password"
              label="Password *"
              fullWidth
            />
          </Grid>
        </Grid>
        <Grid
          className={`${classes.gridItemFlex} ${classes.gridItemFlexSpaceBetween} ${classes.marginTop}`}
          item
          xs={12}
        >
          <div>
            <Checkbox
              color="primary"
              className={classes.checkbox}
              onChange={(event) => {
                setShouldSaveSession(event.target.checked);
              }}
              value={shouldSaveSession}
            />
            <Typography display="inline" variant="body2">
              Ricordami
            </Typography>
          </div>
          {!hideForgottenPassword && (
            <div>
              <ButtonBase>
                <Typography variant="body2">
                  <a
                    href="https://club.giocamondo.it/password-recovery"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classes.forgottenPasswordLabel}
                  >
                    Password dimenticata?
                  </a>
                </Typography>
              </ButtonBase>
            </div>
          )}
        </Grid>
        {!!error && (
          <Grid item xs={12}>
            <Alert severity="error" className={classes.alert}>
              {errorMessage}
            </Alert>
          </Grid>
        )}
      </Grid>
      <MembershipActionFooter
        captionText={captionText}
        onPrimaryAction={handleLoginClick}
        onSecondaryAction={onSecondaryAction}
        isPrimaryActionDisabled={isLoginDisabled()}
        primaryActionText="ACCEDI"
        secondaryActionText={secondaryActionText}
        isPerformingPrimaryAction={isSigningIn}
      />
    </div>
  );
}

MembershipLogin.propTypes = {
  onLogin: PropTypes.func.isRequired,
  onSignup: PropTypes.func.isRequired,
  isSigningIn: PropTypes.bool,
  hideForgottenPassword: PropTypes.bool,
  errors: PropTypes.object,
};
